<svelte:options runes={true} />

<script lang="ts">
  import { point, Size } from '@geom';
  import type { TitleEditorState } from '@reactive';
  import { platformManager } from '@reactive';
  import { PlatformId } from '@strings';
  import { baseKeymap, splitBlock } from 'prosemirror-commands';
  import { history, redo, undo } from 'prosemirror-history';
  import { keymap } from 'prosemirror-keymap';
  import { Schema } from 'prosemirror-model';
  import {
    EditorState,
    TextSelection,
    type Command,
    type EditorStateConfig
  } from 'prosemirror-state';
  import { EditorView } from 'prosemirror-view';
  import 'prosemirror-view/style/prosemirror.css';
  import { onMount } from 'svelte';

  let {
    titleEditorState = $bindable()
  }: {
    titleEditorState: TitleEditorState;
  } = $props();

  let editorElement: HTMLElement;
  let overlayElement: HTMLElement;

  // Scheme that only allows only paragraph and text nodes.
  const schema = new Schema({
    nodes: {
      doc: { content: 'block+' },
      paragraph: {
        content: 'inline*',
        group: 'block',
        toDOM() {
          return ['p', 0];
        },
        parseDOM: [{ tag: 'p' }]
      },
      text: { group: 'inline' }
    }
  });
  let editorView: EditorView;

  const customKeymap: Record<string, Command> = {
    // 'Escape': () => {
    //   cancel();
    //   return true;
    // },
    Enter: () => {
      commit();
      return true;
    }
  };

  if (platformManager.platformId === PlatformId.MAC) {
    customKeymap['Alt-Enter'] = splitBlock;
    customKeymap['Cmd-z'] = undo;
    customKeymap['Cmd-Shift-z'] = redo;
  } else {
    customKeymap['Shift-Enter'] = splitBlock;
    customKeymap['Ctrl-z'] = undo;
    customKeymap['Ctrl-y'] = redo;
  }

  onMount(() => {
    const editorState = EditorState.create({
      schema,
      plugins: [history(), keymap(customKeymap), keymap(baseKeymap)]
    });

    editorView = new EditorView(editorElement, { state: editorState });

    // Add a listener for Tab. We want to execute the commit action
    // and prevent other listeners from receiving it, which for instance
    // prevents the editor from popping right back up again.
    const commitOnTab = (event: KeyboardEvent) => {
      if (event.key === 'Tab') {
        event.preventDefault();
        event.stopPropagation();
        commit();
      }
    };
    const cancelOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        cancel();
      }
    };
    editorElement.addEventListener('keydown', commitOnTab);
    editorElement.addEventListener('keydown', cancelOnEscape);
    return () => {
      editorElement.removeEventListener('keydown', commitOnTab);
      editorElement.removeEventListener('keydown', cancelOnEscape);
    };
  });

  $effect(() => {
    if (titleEditorState.isVisible) {
      show();
    } else {
      hide();
    }
  });

  let editorPos = $derived(
    point(titleEditorState.editorBounds.left, titleEditorState.editorBounds.top)
  );
  let editorSize = $derived(
    new Size(titleEditorState.editorBounds.width, titleEditorState.editorBounds.height)
  );

  let isSetup = false;

  function cancel() {
    titleEditorState.isVisible = false;
  }

  function commit() {
    titleEditorState.title = getEditorContent();
    titleEditorState.change = titleEditorState.change + 1;
    titleEditorState.isVisible = false;
  }

  function setEditorContent(content: string) {
    // Split the content into lines to create paragraphs
    const paragraphs = content
      .split('\n')
      .map(line => schema.node('paragraph', null, schema.text(line)));

    // Create a new document node with the paragraphs
    const doc = schema.node('doc', null, paragraphs);

    // Create a new EditorState with the new document
    const config: EditorStateConfig = {
      doc,
      plugins: editorView.state.plugins // Keep the existing plugins
    };
    const newState = EditorState.create(config);

    // Update the editor view with the new state
    editorView.updateState(newState);
  }

  function getEditorContent(): string {
    const doc = editorView.state.doc;
    const content = doc.content.content.map(node => node.textContent).join('\n');
    return content;
  }

  function selectAll() {
    const transaction = editorView.state.tr.setSelection(
      TextSelection.create(editorView.state.doc, 0, editorView.state.doc.content.size)
    );
    editorView.dispatch(transaction);
  }

  function focus() {
    editorView.focus();
  }

  // Don't call directly, use titleEditorState.isVisible = true
  function show() {
    setEditorContent(titleEditorState.title);
    selectAll();
    focus();

    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    editorElement.offsetHeight; // Trigger reflow to reset animation
    editorElement.style.animation = 'editorFadeIn 0.2s ease-in-out forwards';
    editorElement.classList.add('visible');
    overlayElement.style.animation = 'overlayFadeIn 0.2s ease-in-out forwards';
    overlayElement.classList.add('visible');
  }

  // Don't call directly, use titleEditorState.isVisible = false
  function hide() {
    if (!isSetup) {
      isSetup = true;
      return;
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    editorElement.offsetHeight; // Trigger reflow to reset animation
    editorElement.style.animation = 'editorFadeOut 0.2s ease-in-out forwards';
    editorElement.classList.remove('visible');
    overlayElement.style.animation = 'overlayFadeOut 0.2s ease-in-out forwards';
    overlayElement.classList.remove('visible');
  }

  function handleOverlayClick() {
    titleEditorState.isVisible = false;
  }

  function pointerEvents() {
    return titleEditorState.isVisible ? 'auto' : 'none';
  }
</script>

<style>
  :global {
    @keyframes editorFadeIn {
      0% {
        opacity: 0;
        transform: scale(0.8);
      }
      100% {
        opacity: 1;
        transform: scale(1);
      }
    }

    @keyframes editorFadeOut {
      0% {
        opacity: 1;
        transform: scale(1);
      }
      100% {
        opacity: 0;
        transform: scale(0.8);
      }
    }

    @keyframes overlayFadeIn {
      0% {
        opacity: 0;
      }
      100% {
        opacity: 1;
      }
    }

    @keyframes overlayFadeOut {
      0% {
        opacity: 1;
      }
      100% {
        opacity: 0;
      }
    }
  }

  .overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.2);
    z-index: 4;
    opacity: 0;
  }

  .editor {
    position: absolute;
    z-index: 5;
    border-radius: 10px;
    border: 4px solid var(--fl-orange-500);
    user-select: none;
    text-align: left;
    font-weight: bold;
    opacity: 0;
  }

  .editor::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: var(--sl-color-neutral-0);
    opacity: 0.9;
    border-radius: 0.25rem;
    z-index: -1;
  }

  :global(.editor .ProseMirror) {
    padding: 20px;
    max-height: var(--prosemirror-max-height);
    overflow-y: auto;
  }

  :global(.ProseMirror:focus) {
    outline: none;
  }
</style>

<!-- svelte-ignore a11y_click_events_have_key_events -->
<!-- svelte-ignore a11y_no_static_element_interactions -->
<div
  bind:this={overlayElement}
  style="pointer-events: {pointerEvents()};}"
  class="overlay"
  onclick={handleOverlayClick}
></div>

<div
  bind:this={editorElement}
  style="
  left: {editorPos.x}px;
  top: {editorPos.y}px;
  width: {editorSize.width}px;
  max-width: {editorSize.width}px;
  max-height: {editorSize.height}px;
  min-width: {editorSize.width}px;
  pointer-events: {pointerEvents()};
  --prosemirror-max-height: {editorSize.height - 60}px;
"
  class="editor"
></div>
